/**
 * Client-side email sanity check for invite fields.
 *
 * Mirrors the server guard (invite.service.assertValidInviteEmail): basic
 * shape + no '+' in the DOMAIN part. That domain-plus rule is the one that
 * catches the real prod typo — "sonya@testadmin+sonyameloff.com", the @ and +
 * transposed — while still accepting legitimate plus-addressing like
 * "sonya+testadmin@sonyameloff.com" (+ in the local part), which the whole QA
 * workflow relies on.
 *
 * This is a friendliness layer, not the enforcement point — the server still
 * rejects malformed addresses regardless. So it stays deliberately loose:
 * better to occasionally pass something odd to the server (which will reject
 * it clearly) than to block a valid address the regex didn't anticipate.
 */
export function isLikelyValidEmail(raw: string): boolean {
  const email = raw.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false;
  // A '+' anywhere after the @ means it's in the domain — always a typo.
  if (/@[^@]*\+/.test(email)) return false;
  return true;
}

/** Field-level message, or null when the value looks fine (or is empty — an
 *  empty field is "required", handled separately, not "invalid"). */
export function inviteEmailError(raw: string): string | null {
  if (!raw.trim()) return null;
  return isLikelyValidEmail(raw) ? null : "That doesn't look like a valid email — check for a typo (e.g. the @ and + swapped).";
}
